/**
 * Knowledge-source derivation + upload rules for an AI agent — pure and shared.
 *
 * Same rationale as `widgetConfig.ts`: the row → table shaping has real fallback
 * ladders (documents arrive in three different shapes depending on how old the
 * agent is), and P1.2b needs the identical shaping in the Card-Theme editor
 * drawer that `AiAgentDetail.tsx` already does inline. Two copies would drift,
 * and a drift here means a document that is visible on one screen and
 * undeletable on the other.
 *
 * 🔴 Do NOT re-inline any of this. If the API grows a fourth document shape, it
 * gets handled here once.
 */
import type { AiAgent } from '@/hooks/useAiAgents';

export type WebsiteItem = {
  id: string;
  name: string;
  url: string;
  status: string;
};

export type FileItem = {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
  status: string;
};

export type QaItem = {
  id: string;
  question: string;
  answer: string;
  updatedAt: string;
};

/** Host without `www.`, falling back to the raw string for unparseable URLs. */
export function websiteDisplayName(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '') || url;
  } catch {
    return url;
  }
}

/**
 * An agent carries at most ONE website (`agent.website_url`), so the table is a
 * 0-or-1 row list. The synthetic id `primary` matches what the detail page uses
 * and is what the delete/edit handlers key off.
 */
export function deriveWebsiteItems(agent: AiAgent | null | undefined): WebsiteItem[] {
  const url = agent?.website_url;
  if (!url) return [];
  return [{ id: 'primary', name: websiteDisplayName(url), url, status: 'Available' }];
}

export function deriveQaItems(agent: AiAgent | null | undefined): QaItem[] {
  if (!Array.isArray(agent?.qa_data)) return [];
  return agent.qa_data
    .filter((q): q is { id?: string; question: string; answer: string } =>
      !!q && typeof q === 'object' && typeof (q as any).question === 'string')
    .map((q, index) => ({
      id: q.id || `${index}-${q.question}`,
      question: q.question,
      answer: typeof q.answer === 'string' ? q.answer : '',
      updatedAt: agent?.updated_at || '',
    }));
}

/**
 * Documents arrive in three shapes, in priority order:
 *   1. `agent.documents[]` — the only shape carrying the real document id, which
 *      deletion REQUIRES. Always prefer it.
 *   2. `document_ids[]` paired positionally with `document_paths[]`.
 *   3. `document_paths[]` alone — ids are synthesised (`doc-<i>`), so rows from
 *      this shape cannot be deleted by id; the UI still lists them so the file
 *      is at least visible.
 *
 * `size` is 0 because no shape reports it — callers must not render a size for
 * these rows rather than print "0 B".
 */
export function deriveFileItems(agent: AiAgent | null | undefined): FileItem[] {
  if (!agent) return [];
  const uploadedAt = agent.updated_at || agent.created_at || '';

  if (Array.isArray(agent.documents) && agent.documents.length > 0) {
    return agent.documents.map((doc, index) => ({
      id: doc.id,
      name:
        doc.document_name
        || doc.document_path?.split('/').pop()
        || `document-${index + 1}`,
      size: 0,
      uploadedAt,
      status: 'Available',
    }));
  }

  if (Array.isArray(agent.document_paths) && agent.document_paths.length > 0) {
    return agent.document_paths.map((path, index) => ({
      id: agent.document_ids?.[index] || `doc-${index}`,
      name: path.split('/').pop() || `document-${index + 1}`,
      size: 0,
      uploadedAt,
      status: 'Available',
    }));
  }

  return [];
}

/** Rows whose id was synthesised cannot be deleted server-side. */
export function isDeletableFile(item: FileItem): boolean {
  return !item.id.startsWith('doc-') && !item.id.startsWith('temp-');
}

// ── Upload rules ───────────────────────────────────────────────────────────
// Mirrors the detail page exactly. ONE document per agent is a real backend
// constraint, not a UI preference.

export const MAX_DOCUMENT_BYTES = 100 * 1024 * 1024; // 100MB
export const MAX_DOCUMENTS_PER_AGENT = 1;
export const DOCUMENT_ACCEPT =
  '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const MAX_TEXT_SOURCE_CHARS = 3000;

const PDF_MIME = 'application/pdf';
const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * Type check accepts either the MIME type OR the extension — some browsers send
 * an empty/incorrect `type` for .docx, so extension-only files must still pass.
 */
export function isSupportedDocument(file: { name: string; type: string }): boolean {
  return (
    file.type === PDF_MIME
    || file.type === DOCX_MIME
    || /\.pdf$/i.test(file.name)
    || /\.docx$/i.test(file.name)
  );
}

export type DocumentRejection = { file: string; reason: 'type' | 'size' };

/**
 * Validate a selection against the per-agent limit, type and size rules.
 * Returns the accepted file (at most one) plus a rejection reason per file so
 * the caller can explain each failure individually, as the detail page does.
 */
export function validateDocumentSelection(
  files: Array<{ name: string; type: string; size: number }>,
  existingCount: number,
): {
  accepted: Array<{ name: string; type: string; size: number }>;
  rejected: DocumentRejection[];
  limitReached: boolean;
} {
  if (existingCount >= MAX_DOCUMENTS_PER_AGENT) {
    return { accepted: [], rejected: [], limitReached: true };
  }
  const rejected: DocumentRejection[] = [];
  const accepted: Array<{ name: string; type: string; size: number }> = [];

  // Only ever one document, even if a multi-select slipped through.
  for (const file of files.slice(0, MAX_DOCUMENTS_PER_AGENT)) {
    if (!isSupportedDocument(file)) {
      rejected.push({ file: file.name, reason: 'type' });
      continue;
    }
    if (file.size > MAX_DOCUMENT_BYTES) {
      rejected.push({ file: file.name, reason: 'size' });
      continue;
    }
    accepted.push(file);
  }

  return { accepted, rejected, limitReached: false };
}

/** Normalise a typed website URL, rejecting anything `URL` cannot parse. */
export function normalizeWebsiteUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(candidate).href;
  } catch {
    return null;
  }
}

/** Case-insensitive filter across a knowledge list, matching the detail page. */
export function filterKnowledge<T extends Record<string, unknown>>(
  items: T[],
  query: string,
  fields: Array<keyof T>,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) =>
    fields.some((f) => String(item[f] ?? '').toLowerCase().includes(q)),
  );
}

/** Chips shown above the test-panel composer. Both fields are loosely typed. */
export function deriveSuggestions(agent: AiAgent | null | undefined): {
  questions: string[];
  buttons: Array<{ label: string; action: string }>;
} {
  const questions = Array.isArray(agent?.suggested_questions)
    ? agent.suggested_questions.filter((q): q is string => typeof q === 'string' && !!q.trim())
    : [];
  const buttons = Array.isArray(agent?.suggested_buttons)
    ? agent.suggested_buttons
        .filter((b): b is { label: string; action: string } =>
          !!b && typeof b === 'object' && typeof (b as any).label === 'string')
        .map((b) => ({ label: b.label, action: typeof b.action === 'string' ? b.action : '' }))
    : [];
  return { questions, buttons };
}

/** One starter chip in the agent test panel. */
export interface SuggestionChip {
  key: string;
  /** Text shown on the chip. */
  label: string;
  /** Text SENT to the agent when the chip is clicked. */
  ask: string;
}

/**
 * Starter chips for the test panel, built from an agent's suggested buttons and
 * questions.
 *
 * 🔴 `label` and `ask` are assigned from the SAME value, deliberately.
 *
 * A suggested button carries a human `label` ("I want to learn more about your
 * AI chatbot") plus an `action`, which is the widget's internal routing key
 * ("action1", "action2", …). The test panel used to ask with
 * `action || label` — action FIRST — so clicking a chip posted "action1" into
 * the transcript as though the tester had typed it, and the agent answered a
 * meaningless token.
 *
 * The rest of the app already sends the label: `AiAgentDetail` uses
 * `button.label`, and the production `WhatsAppChatWidget` uses
 * `button.label || button.action`. A test panel that sends something else is
 * not testing what a visitor experiences, which is its only job.
 *
 * `action` survives only as the fallback for a button saved with a blank label,
 * mirroring the widget's `||`. A chip with neither is dropped rather than
 * rendered blank and unsendable.
 */
export function buildSuggestionChips(
  agent: AiAgent | null | undefined,
  max = 8,
): SuggestionChip[] {
  const { questions, buttons } = deriveSuggestions(agent);
  const chips: SuggestionChip[] = [
    ...buttons.map((b, i) => {
      const text = (b.label || '').trim() || (b.action || '').trim();
      return { key: `b-${i}`, label: text, ask: text };
    }),
    ...questions.map((q, i) => {
      const text = q.trim();
      return { key: `q-${i}`, label: text, ask: text };
    }),
  ].filter((c) => c.ask.length > 0);

  const limit = Number.isFinite(max) && max > 0 ? Math.trunc(max) : chips.length;
  return chips.slice(0, limit);
}

// ── Q&A wire shape ─────────────────────────────────────────────────────────

/**
 * The EXACT object shape `/ai-agent/add-qa` and `PUT /ai-agent/:id/qa` accept.
 *
 * 🔴 `id` is deliberately absent. The create drawer and the detail page both
 * send `{ question, answer }` only, and those are the two paths known to work;
 * the ids surfaced by `deriveQaItems` are frequently SYNTHETIC
 * (`${index}-${question}` when the row carries none), so forwarding them would
 * persist a made-up identifier as real data.
 */
export type QaPairPayload = { question: string; answer: string };

/** Strings pass through; a number is stringified; anything else is empty. */
function qaText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value).trim();
  return '';
}

/**
 * Coerce whatever a caller hands us into the array-of-pairs the microservice
 * demands, dropping anything unusable.
 *
 * 🔴 THE SINGLE-OBJECT BRANCH IS THE WHOLE POINT. The Q&A tab of the edit
 * drawer called `addQa.mutateAsync({ question, answer } as any)` — one pair, not
 * a list. `postFormUrlEncoded` happily `JSON.stringify`s any object, so the wire
 * carried `qa_data={"question":…,"answer":…}` and FastAPI answered
 * `400 {"detail":"Q&A data must be a JSON array"}`. Creating an agent worked
 * because that path passes `qaItems.map(...)`, which is already an array — so the
 * feature looked fine everywhere except editing. Normalising at the one choke
 * point means no future caller can reintroduce it, whatever the `as any` says.
 *
 * 🔴 THE REPLACE PATH MUST NOT DROP ANYTHING IT CAN REPRESENT. `PUT …/qa`
 * rewrites the entire array, so anything this function omits is DELETED — from a
 * request the user issued to edit one unrelated pair, reported as success. That
 * is why the default mode keeps a pair with a blank question OR a blank answer
 * (`deriveQaItems` renders both, so they are visible rows the user would watch
 * vanish), and why de-duplication keys on question AND answer: two pairs sharing
 * a question but answering differently are both real, and the UI deliberately
 * permits them (`qaPairExists` compares both fields too).
 *
 * @param requireAnswer `true` for the APPEND endpoint (`add-qa`) — nothing is at
 *   risk there, so it enforces the UI's own rule that a new pair needs both
 *   halves. `false` (default) for the REPLACE endpoint, per the paragraph above.
 */
export function normalizeQaPairs(
  input: unknown,
  { requireAnswer = false }: { requireAnswer?: boolean } = {},
): QaPairPayload[] {
  if (input === null || input === undefined) return [];

  const list = Array.isArray(input) ? input : [input];
  const out: QaPairPayload[] = [];
  const seen = new Set<string>();

  for (const raw of list) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;

    const question = qaText((raw as { question?: unknown }).question);
    const answer = qaText((raw as { answer?: unknown }).answer);

    if (requireAnswer) {
      // Append: the UI's own rule — a pair missing either half is junk.
      if (!question || !answer) continue;
    } else if (!question && !answer) {
      // Replace: keep everything EXCEPT a row with no content at all. There is
      // nothing to preserve in one, and sending `{question:"",answer:""}` risks
      // the service rejecting the whole array — which would block editing for
      // every other pair on the agent.
      continue;
    }

    // Within ONE request the exact same pair twice is always a mistake (a
    // double-submit, or two identical rows staged in the create drawer). Keyed on
    // BOTH fields, so `hours → 9 to 5` and `hours → 10 to 6` both survive. Keep
    // the first occurrence. Keyed via JSON, so no separator can collide with
    // typed text.
    const key = JSON.stringify([question.toLowerCase(), answer.toLowerCase()]);
    if (seen.has(key)) continue;
    seen.add(key);

    out.push({ question, answer });
  }

  return out;
}

/** Is this pair already in the list? Question+answer, both case-insensitive. */
export function qaPairExists(
  items: Array<{ question: string; answer: string }>,
  pair: { question: string; answer: string },
): boolean {
  const q = pair.question.trim().toLowerCase();
  const a = pair.answer.trim().toLowerCase();
  return items.some(
    (item) =>
      (item.question || '').trim().toLowerCase() === q
      && (item.answer || '').trim().toLowerCase() === a,
  );
}

// ── Instagram binding ──────────────────────────────────────────────────────

/**
 * The only part of an Instagram flow the binding check reads.
 *
 * Structurally typed rather than importing `InstagramFlow`, so this pure module
 * stays free of the flows API surface (and of its `config.graph` type churn).
 */
export type InstagramFlowLike = {
  id?: string | null;
  name?: string | null;
  is_enabled?: boolean | null;
  config?: {
    graph?: { nodes?: Array<{ type?: string | null; data?: unknown }> | null } | null;
  } | null;
};

/**
 * Is this agent bound as the knowledge source of an `ai_response` node in this
 * flow?
 *
 * 🔴 THE BINDING LIVES ON THE FLOW NODE, NOT ON THE AGENT. There is no
 * `instagram_connected` column and no `/ai-agent/connect` equivalent for
 * Instagram — an agent "is" an Instagram agent exactly when some flow references
 * it. So this predicate IS the source of truth, and it must stay identical to
 * the one behind the "Agent connected" pill in `AgentChannelsPanel`: a card
 * badge that disagrees with the drawer badge is worse than no badge.
 *
 * All three conditions are load-bearing. `useAgent === true` is strict because
 * the checkbox can be off while a stale `agentId` remains in the node data —
 * that node does NOT query the agent. And the id must match THIS agent, or every
 * agent would light up for a flow bound to one of them.
 */
export function isAgentBoundToInstagramFlow(
  flow: InstagramFlowLike | null | undefined,
  agentId: string | null | undefined,
): boolean {
  if (!flow || !agentId) return false;
  const nodes = flow.config?.graph?.nodes;
  if (!Array.isArray(nodes)) return false;
  return nodes.some((node) => {
    if (!node || node.type !== 'ai_response') return false;
    const knowledge = (node.data as { knowledge?: Record<string, unknown> } | undefined)
      ?.knowledge;
    return knowledge?.useAgent === true && knowledge?.agentId === agentId;
  });
}

/** One flow an agent is bound to, for the card pill's tooltip. */
export type InstagramAgentBinding = {
  flowId: string;
  flowName: string;
  /** `false` = the flow exists and references the agent but is paused. */
  enabled: boolean;
};

/**
 * agentId → the flows binding it. Built once per flow list rather than scanned
 * per card, so a grid of N agents costs one pass instead of N.
 *
 * An agent bound by the SAME flow twice (two `ai_response` nodes) appears once —
 * the map is keyed by flow, and the pill answers "is it used here", not "how
 * many nodes".
 */
export function buildInstagramAgentBindings(
  flows: InstagramFlowLike[] | null | undefined,
  agentIds: Iterable<string>,
): Map<string, InstagramAgentBinding[]> {
  const out = new Map<string, InstagramAgentBinding[]>();
  if (!Array.isArray(flows) || flows.length === 0) return out;

  for (const agentId of agentIds) {
    if (!agentId) continue;
    for (const flow of flows) {
      if (!isAgentBoundToInstagramFlow(flow, agentId)) continue;
      const list = out.get(agentId) ?? [];
      list.push({
        flowId: String(flow.id ?? ''),
        // A flow saved without a name still has to be nameable in the tooltip.
        flowName: (flow.name || '').trim() || 'Untitled flow',
        enabled: flow.is_enabled !== false,
      });
      out.set(agentId, list);
    }
  }

  return out;
}
